import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';
import { User, GraduationCap, FileText, Users, Phone, Mail, MapPin, Edit3, Save, Loader2, Eye, EyeOff, Lock } from 'lucide-react';
import { fetchAddressByCEP } from '../services/cepService';
import { validateCPF, validatePhone, validateEmail, validateRG } from '../utils/validations';
import { useAuth } from '../contexts/AuthContext';
import ImageUpload from './ImageUpload';
import 'react-datepicker/dist/react-datepicker.css';

const schema = yup.object().shape({
  // Dados Pessoais
  firstName: yup.string().required('Nome é obrigatório').min(2, 'Nome deve ter pelo menos 2 caracteres'),
  lastName: yup.string().required('Sobrenome é obrigatório').min(2, 'Sobrenome deve ter pelo menos 2 caracteres'),
  birthDate: yup.date().required('Data de nascimento é obrigatória').test('is-adult', 'Deve ser maior de idade', function(value) {
    if (!value) return false;
    const today = new Date();
    const age = today.getFullYear() - value.getFullYear();
    const monthDiff = today.getMonth() - value.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < value.getDate())) {
      return age - 1 >= 18;
    }
    return age >= 18;
  }).test('not-future', 'Data não pode ser futura', function(value) {
    if (!value) return false;
    return value <= new Date();
  }),
  gender: yup.string().required('Sexo é obrigatório'),
  
  // Escolaridade e Estado Civil
  education: yup.string().required('Escolaridade é obrigatória'),
  maritalStatus: yup.string().required('Estado civil é obrigatório'),
  
  // Documentos
  document1: yup.string()
    .required('CPF é obrigatório')
    .test('valid-cpf', 'CPF inválido', validateCPF),
  document2: yup.string()
    .required('RG é obrigatório')
    .test('valid-rg', 'RG inválido', validateRG),
  
  // Outras Informações
  group: yup.string().required('Grupo é obrigatório'),
  categories: yup.string().required('Categoria é obrigatória'),
  roles: yup.string().required('Cargo é obrigatório'),
  conversionDate: yup.string().required('Data de conversão é obrigatória'),
  baptismDate: yup.string().optional(),
  
  // Contatos
  phone1: yup.string()
    .required('Telefone 1 é obrigatório')
    .test('valid-phone', 'Telefone inválido', validatePhone),
  phone2: yup.string()
    .test('valid-phone', 'Telefone inválido', function(value) {
      if (!value) return true; // opcional
      return validatePhone(value);
    }),
  email: yup.string()
    .required('E-mail é obrigatório')
    .email('E-mail inválido')
    .test('valid-email', 'E-mail inválido', validateEmail),
  
  // Senha
  password: yup.string()
    .required('Senha é obrigatória')
    .min(6, 'Senha deve ter pelo menos 6 caracteres'),
  confirmPassword: yup.string()
    .required('Confirmação de senha é obrigatória')
    .oneOf([yup.ref('password'), null], 'Senhas devem ser iguais'),
  
  // Endereço
  zipCode: yup.string()
    .required('CEP é obrigatório')
    .test('valid-cep', 'CEP inválido', function(value) {
      if (!value) return false;
      const cleanCEP = value.replace(/\D/g, '');
      return cleanCEP.length === 8;
    }),
  address: yup.string().required('Endereço é obrigatório'),
  number: yup.string().required('Número é obrigatório'),
  neighborhood: yup.string().required('Bairro é obrigatório'),
  city: yup.string().required('Cidade é obrigatória'),
  country: yup.string().required('País é obrigatório'),
  state: yup.string().required('Estado é obrigatório'),
  
  // Anotações
  notes: yup.string().optional(),
  
  // Foto
  photo: yup.mixed()
});

const RegistrationForm = ({ onToggleMode }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [addressData, setAddressData] = useState(null);
  const [isLoadingCEP, setIsLoadingCEP] = useState(false);
  const [notesReadOnly, setNotesReadOnly] = useState(true);
  
  const { register } = useAuth();
  
  const totalSteps = 3;
  
  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      notes: ''
    }
  });

  const watchedZipCode = watch('zipCode');

  React.useEffect(() => {
    if (watchedZipCode && watchedZipCode.replace(/\D/g, '').length === 8) {
      // Adicionar um pequeno delay para evitar muitas chamadas
      const timeoutId = setTimeout(() => {
        handleCEPChange(watchedZipCode);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [watchedZipCode]);

  // Garantir que o campo de anotações esteja vazio ao montar o componente
  React.useEffect(() => {
    setValue('notes', '');
  }, [setValue]);

  const handleCEPChange = async (cep) => {
    if (!cep || cep.replace(/\D/g, '').length !== 8) return;
    
    setIsLoadingCEP(true);
    try {
      const data = await fetchAddressByCEP(cep);
      if (data && !data.erro) {
        setAddressData(data);
        setValue('address', data.logradouro || '');
        setValue('neighborhood', data.bairro || '');
        setValue('city', data.localidade || '');
        setValue('state', data.uf || '');
        setValue('country', 'Brasil');
        
        // Trigger validation for the filled fields
        await trigger(['address', 'neighborhood', 'city', 'state', 'country']);
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      // Limpar os campos se houver erro
      setValue('address', '');
      setValue('neighborhood', '');
      setValue('city', '');
      setValue('state', '');
      setValue('country', '');
    } finally {
      setIsLoadingCEP(false);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Preparar dados para a API
      const formData = {
        ...data,
        // Mapear campos para o formato da API
        nome: data.firstName,
        sobrenome: data.lastName,
        dataNascimento: data.birthDate ? data.birthDate.toISOString().split('T')[0] : null, // Formato YYYY-MM-DD
        sexo: data.gender,
        escolaridade: data.education,
        estadoCivil: data.maritalStatus,
        documento1: data.document1,
        documento2: data.document2,
        grupo: data.group,
        categorias: data.categories,
        cargos: data.roles,
        dataConversao: data.conversionDate,
        dataBaptismo: data.baptismDate,
        telefone1: data.phone1,
        telefone2: data.phone2,
        email: data.email,
        senha: data.password,
        endereco: data.address,
        numero: data.number,
        bairro: data.neighborhood,
        cidade: data.city, // Adicionando campo cidade
        cep: data.zipCode,
        pais: data.country,
        estado: data.state,
        anotacoes: data.notes
      };

      await register(formData);
      alert('Cadastro realizado com sucesso!');
    } catch (error) {
      console.error('Erro no cadastro:', error);
      alert('Erro ao realizar cadastro. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = async () => {
    const fieldsToValidate = getFieldsForStep(currentStep);
    const isValid = await trigger(fieldsToValidate);
    
    // Validação manual para data de conversão
    if (isValid && currentStep === 1) {
      const conversionDate = watch('conversionDate');
      const baptismDate = watch('baptismDate');
      
      if (conversionDate === 'sim' && !baptismDate) {
        alert('Por favor, selecione a data da conversão.');
        return;
      }
    }
    
    if (isValid) {
      const nextStepNumber = Math.min(currentStep + 1, totalSteps);
      setCurrentStep(nextStepNumber);
      
      // Se estiver indo para a etapa 3 (anotações), garantir que o campo esteja vazio
      if (nextStepNumber === 3) {
        setValue('notes', '');
        setNotesReadOnly(true);
      }
    }
  };

  const handleFinalSubmit = async () => {
    // Na etapa 3, não validar o campo notes antes de submeter
    const formData = watch();
    await onSubmit(formData);
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const getFieldsForStep = (step) => {
    switch (step) {
      case 1:
        return ['firstName', 'lastName', 'birthDate', 'gender', 'education', 'maritalStatus', 'document1', 'document2', 'group', 'categories', 'roles', 'conversionDate', 'baptismDate', 'phone1', 'phone2', 'email', 'password', 'confirmPassword'];
      case 2:
        return ['zipCode', 'address', 'number', 'neighborhood', 'city', 'country', 'state'];
      case 3:
        return ['notes'];
      default:
        return [];
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Dados Pessoais */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center mb-4">
                <User className="w-5 h-5 text-primary-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Dados Pessoais</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Nome *</label>
                  <input
                    type="text"
                    {...registerField('firstName')}
                    className={`input-field ${errors.firstName ? 'input-error' : ''}`}
                    placeholder="Digite seu nome"
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName.message}</span>}
                </div>
                
                <div>
                  <label className="label">Sobrenome *</label>
                  <input
                    type="text"
                    {...registerField('lastName')}
                    className={`input-field ${errors.lastName ? 'input-error' : ''}`}
                    placeholder="Digite seu sobrenome"
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName.message}</span>}
                </div>
                
                <div>
                  <label className="label">Data de Nascimento *</label>
                  <DatePicker
                    selected={watch('birthDate')}
                    onChange={(date) => setValue('birthDate', date)}
                    dateFormat="dd/MM/yyyy"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    placeholderText="DD/MM/AAAA"
                    className={`input-field ${errors.birthDate ? 'input-error' : ''}`}
                  />
                  {errors.birthDate && <span className="error-message">{errors.birthDate.message}</span>}
                </div>
                
                                 <div>
                   <label className="label">Sexo *</label>
                   <select
                     {...registerField('gender')}
                     className={`input-field ${errors.gender ? 'input-error' : ''}`}
                   >
                     <option value="">Selecione</option>
                     <option value="masculino">Masculino</option>
                     <option value="feminino">Feminino</option>
                   </select>
                   {errors.gender && <span className="error-message">{errors.gender.message}</span>}
                 </div>
              </div>
            </div>

            {/* Escolaridade e Estado Civil */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center mb-4">
                <GraduationCap className="w-5 h-5 text-primary-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Escolaridade e Estado Civil</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 <div>
                   <label className="label">Escolaridade *</label>
                   <select
                     {...registerField('education')}
                     className={`input-field ${errors.education ? 'input-error' : ''}`}
                   >
                     <option value="">Selecione</option>
                     <option value="fundamental_incompleto">Fundamental Incompleto</option>
                     <option value="fundamental_completo">Fundamental Completo</option>
                     <option value="medio_incompleto">Médio Incompleto</option>
                     <option value="medio_completo">Médio Completo</option>
                     <option value="superior_incompleto">Superior Incompleto</option>
                     <option value="superior_completo">Superior Completo</option>
                     <option value="pos_graduacao">Pós-graduação</option>
                   </select>
                   {errors.education && <span className="error-message">{errors.education.message}</span>}
                 </div>
                
                                 <div>
                   <label className="label">Estado Civil *</label>
                   <select
                     {...registerField('maritalStatus')}
                     className={`input-field ${errors.maritalStatus ? 'input-error' : ''}`}
                   >
                     <option value="">Selecione</option>
                     <option value="solteiro">Solteiro(a)</option>
                     <option value="casado">Casado(a)</option>
                     <option value="divorciado">Divorciado(a)</option>
                     <option value="viuvo">Viúvo(a)</option>
                   </select>
                   {errors.maritalStatus && <span className="error-message">{errors.maritalStatus.message}</span>}
                 </div>
              </div>
            </div>

            {/* Documentos */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center mb-4">
                <FileText className="w-5 h-5 text-primary-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Documentos</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">CPF (Documento 1) *</label>
                  <InputMask
                    mask="999.999.999-99"
                    {...registerField('document1')}
                    className={`input-field ${errors.document1 ? 'input-error' : ''}`}
                    placeholder="000.000.000-00"
                  />
                  {errors.document1 && <span className="error-message">{errors.document1.message}</span>}
                </div>
                
                <div>
                  <label className="label">RG (Documento 2) *</label>
                  <InputMask
                    mask="99.999.999-9"
                    {...registerField('document2')}
                    className={`input-field ${errors.document2 ? 'input-error' : ''}`}
                    placeholder="00.000.000-0"
                  />
                  {errors.document2 && <span className="error-message">{errors.document2.message}</span>}
                </div>
              </div>
            </div>

            {/* Outras Informações */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center mb-4">
                <Users className="w-5 h-5 text-primary-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Outras Informações</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Grupo *</label>
                  <select
                    {...registerField('group')}
                    className={`input-field ${errors.group ? 'input-error' : ''}`}
                  >
                    <option value="">Selecione</option>
                    <option value="Nenhum">Nenhum</option>
                    <option value="Grupo 1">Grupo 1</option>
                    <option value="Grupo 2">Grupo 2</option>
                    <option value="Grupo 3">Grupo 3</option>
                  </select>
                  {errors.group && <span className="error-message">{errors.group.message}</span>}
                </div>
                
                                 <div>
                   <label className="label">Categoria *</label>
                   <select
                     {...registerField('categories')}
                     className={`input-field ${errors.categories ? 'input-error' : ''}`}
                   >
                     <option value="">Selecione</option>
                     <option value="frequentador">Frequentador</option>
                     <option value="membro_afastado">Membro Afastado</option>
                     <option value="membro_ativo">Membro Ativo</option>
                   </select>
                   {errors.categories && <span className="error-message">{errors.categories.message}</span>}
                 </div>
                
                                 <div>
                   <label className="label">Cargo *</label>
                   <select
                     {...registerField('roles')}
                     className={`input-field ${errors.roles ? 'input-error' : ''}`}
                   >
                     <option value="">Selecione</option>
                     <option value="facilitador">Facilitador</option>
                     <option value="musico">Músico</option>
                     <option value="secretario">Secretário(a)</option>
                     <option value="tesoureiro">Tesoureiro(a)</option>
                   </select>
                   {errors.roles && <span className="error-message">{errors.roles.message}</span>}
                 </div>
                
                                 <div>
                   <label className="label">Data de Conversão *</label>
                   <div className="space-y-2">
                     <select
                       {...registerField('conversionDate')}
                       className={`input-field ${errors.conversionDate ? 'input-error' : ''}`}
                       onChange={(e) => {
                         const value = e.target.value;
                         setValue('conversionDate', value);
                         if (value === 'nao') {
                           setValue('baptismDate', '');
                         }
                       }}
                     >
                       <option value="">Selecione</option>
                       <option value="sim">Sim, foi convertido</option>
                       <option value="nao">Não foi convertido</option>
                     </select>
                     
                     {watch('conversionDate') === 'sim' && (
                       <div>
                         <label className="label">Data da Conversão</label>
                         <DatePicker
                           selected={watch('baptismDate') ? new Date(watch('baptismDate')) : null}
                           onChange={(date) => setValue('baptismDate', date ? date.toISOString().split('T')[0] : '')}
                           dateFormat="dd/MM/yyyy"
                           showYearDropdown
                           scrollableYearDropdown
                           yearDropdownItemNumber={100}
                           placeholderText="DD/MM/AAAA"
                           className={`input-field ${errors.baptismDate ? 'input-error' : ''}`}
                         />
                         {errors.baptismDate && <span className="error-message">{errors.baptismDate.message}</span>}
                       </div>
                     )}
                   </div>
                   {errors.conversionDate && <span className="error-message">{errors.conversionDate.message}</span>}
                 </div>
              </div>
            </div>

            {/* Contatos */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center mb-4">
                <Phone className="w-5 h-5 text-primary-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Contatos</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Telefone 1 *</label>
                  <InputMask
                    mask="(99) 99999-9999"
                    {...registerField('phone1')}
                    className={`input-field ${errors.phone1 ? 'input-error' : ''}`}
                    placeholder="(00) 00000-0000"
                  />
                  {errors.phone1 && <span className="error-message">{errors.phone1.message}</span>}
                </div>
                
                <div>
                  <label className="label">Telefone 2</label>
                  <InputMask
                    mask="(99) 99999-9999"
                    {...registerField('phone2')}
                    className={`input-field ${errors.phone2 ? 'input-error' : ''}`}
                    placeholder="(00) 00000-0000"
                  />
                  {errors.phone2 && <span className="error-message">{errors.phone2.message}</span>}
                </div>
                
                <div className="md:col-span-2">
                  <label className="label">E-mail *</label>
                  <input
                    type="email"
                    {...registerField('email')}
                    className={`input-field ${errors.email ? 'input-error' : ''}`}
                    placeholder="seu@email.com"
                  />
                  {errors.email && <span className="error-message">{errors.email.message}</span>}
                </div>
              </div>
            </div>

            {/* Senha */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center mb-4">
                <Lock className="w-5 h-5 text-primary-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Senha</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Senha *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...registerField('password')}
                      className={`input-field pr-10 ${errors.password ? 'input-error' : ''}`}
                      placeholder="Digite sua senha"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <span className="error-message">{errors.password.message}</span>}
                </div>
                
                <div>
                  <label className="label">Confirmar Senha *</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...registerField('confirmPassword')}
                      className={`input-field pr-10 ${errors.confirmPassword ? 'input-error' : ''}`}
                      placeholder="Confirme sua senha"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword.message}</span>}
                </div>
              </div>
            </div>

            {/* Foto */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <ImageUpload label="Foto" required={false} />
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center mb-6">
              <MapPin className="w-5 h-5 text-primary-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Endereço</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">CEP *</label>
                <InputMask
                  mask="99999-999"
                  {...registerField('zipCode')}
                  className={`input-field ${errors.zipCode ? 'input-error' : ''}`}
                  placeholder="00000-000"
                />
                                 {isLoadingCEP && <span className="text-sm text-blue-600">Buscando endereço...</span>}
                 {addressData && !isLoadingCEP && <span className="text-sm text-green-600">Endereço encontrado!</span>}
                 {errors.zipCode && <span className="error-message">{errors.zipCode.message}</span>}
              </div>
              
              <div className="md:col-span-2">
                <label className="label">Endereço *</label>
                <input
                  type="text"
                  {...registerField('address')}
                  className={`input-field ${errors.address ? 'input-error' : ''}`}
                  placeholder="Digite o endereço"
                />
                {errors.address && <span className="error-message">{errors.address.message}</span>}
              </div>
              
              <div>
                <label className="label">Número *</label>
                <input
                  type="text"
                  {...registerField('number')}
                  className={`input-field ${errors.number ? 'input-error' : ''}`}
                  placeholder="Digite o número"
                />
                {errors.number && <span className="error-message">{errors.number.message}</span>}
              </div>
              
                             <div>
                 <label className="label">Bairro *</label>
                 <input
                   type="text"
                   {...registerField('neighborhood')}
                   className={`input-field ${errors.neighborhood ? 'input-error' : ''}`}
                   placeholder="Digite o bairro"
                 />
                 {errors.neighborhood && <span className="error-message">{errors.neighborhood.message}</span>}
               </div>
               
               <div>
                 <label className="label">Cidade *</label>
                 <input
                   type="text"
                   {...registerField('city')}
                   className={`input-field ${errors.city ? 'input-error' : ''}`}
                   placeholder="Digite a cidade"
                 />
                 {errors.city && <span className="error-message">{errors.city.message}</span>}
               </div>
               
               <div>
                 <label className="label">País *</label>
                 <input
                   type="text"
                   {...registerField('country')}
                   className={`input-field ${errors.country ? 'input-error' : ''}`}
                   placeholder="Digite o país"
                 />
                 {errors.country && <span className="error-message">{errors.country.message}</span>}
               </div>
              
              <div>
                <label className="label">Estado *</label>
                <input
                  type="text"
                  {...registerField('state')}
                  className={`input-field ${errors.state ? 'input-error' : ''}`}
                  placeholder="Digite o estado"
                />
                {errors.state && <span className="error-message">{errors.state.message}</span>}
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center mb-6">
              <Edit3 className="w-5 h-5 text-primary-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Anotações</h3>
            </div>
            
            <div>
              <label className="label">Anotações</label>
              <textarea
                {...registerField('notes')}
                rows={6}
                className={`input-field ${errors.notes ? 'input-error' : ''} ${notesReadOnly ? 'bg-gray-50 cursor-pointer' : ''}`}
                placeholder={notesReadOnly ? "Clique aqui para adicionar anotações (opcional)" : "Digite suas anotações (opcional)"}
                autoComplete="off"
                name="member-notes"
                id="member-notes"
                data-form-type="other"
                spellCheck="false"
                readOnly={notesReadOnly}
                onFocus={() => setNotesReadOnly(false)}
                onClick={() => setNotesReadOnly(false)}
              />
              {errors.notes && <span className="error-message">{errors.notes.message}</span>}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-primary-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">Cadastro de Membro</h1>
                <p className="text-primary-100 mt-1">Centro Espirita Fonte de Luz</p>
              </div>
              <button
                onClick={onToggleMode}
                className="text-primary-100 hover:text-white text-sm font-medium"
              >
                Já tem conta? Faça login
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Etapa {currentStep} de {totalSteps}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((currentStep / totalSteps) * 100)}% completo
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>

              <div className="flex space-x-3">
                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-primary"
                  >
                    Próximo
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleFinalSubmit}
                    disabled={isLoading}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Cadastrando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Finalizar Cadastro
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
